/**
 * EventBuilder — create/edit an event and its pre-order form (/events/new,
 * /events/:id/edit). Left column configures basics, menu (items and/or link),
 * custom questions, and settings; the right column shows a live preview of the
 * attendee form (Typeform/Jotform style).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getEvent, createEvent, updateEvent, subscribeMenus, createMenu } from './eventsCloud';
import { blankEvent, newField, importMenuItems } from './eventUtils';
import { FIELD_TYPES } from './fieldTypes';
import FieldEditor from './components/FieldEditor';
import MenuItemsEditor from './components/MenuItemsEditor';
import MenuEditorModal from './components/MenuEditorModal';
import FormPreview from './components/FormPreview';
import ShareModal from './components/ShareModal';
import Modal from './components/Modal';
import EventTimeline from './components/EventTimeline';
import { t } from './strings';
import './Events.css';

// datetime-local input <-> millisecond timestamp
function msToLocalInput(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToMs(v) {
  if (!v) return null;
  const ms = new Date(v).getTime();
  return Number.isNaN(ms) ? null : ms;
}

const SETTING_TOGGLES = [
  'requireName', 'requirePhone', 'requireEmail',
  'oneOrderPerDevice', 'allowInstructions', 'showPrices',
];

export default function EventBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editing = !!id;

  const [ev, setEv] = useState(null);
  const [addType, setAddType] = useState('short_text');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [menus, setMenus] = useState([]);
  const [loadMenuId, setLoadMenuId] = useState('');
  const [showSaveMenu, setShowSaveMenu] = useState(false);

  useEffect(() => {
    let alive = true;
    if (editing) {
      getEvent(id)
        .then((e) => { if (alive) setEv(e || blankEvent()); })
        .catch((e) => { if (alive) { setEv(blankEvent()); setErr(e.message); } });
    } else {
      setEv(blankEvent());
    }
    return () => { alive = false; };
  }, [id, editing]);

  useEffect(() => subscribeMenus(setMenus, () => setMenus([])), []);

  if (!ev) {
    return <div className="ev"><div className="ev__inner"><div className="ev__loading">{t('loading')}</div></div></div>;
  }

  const patch = (p) => setEv((prev) => ({ ...prev, ...p }));
  const patchSettings = (p) => setEv((prev) => ({ ...prev, settings: { ...prev.settings, ...p } }));

  const loadMenu = () => {
    const m = menus.find((x) => x.id === loadMenuId);
    if (!m) return;
    patch({ menuItems: [...(ev.menuItems || []), ...importMenuItems(m.items)] });
    setLoadMenuId('');
  };

  const addField = () => patch({ fields: [...(ev.fields || []), newField(addType)] });
  const updateField = (idx, f) => patch({ fields: ev.fields.map((x, i) => (i === idx ? f : x)) });
  const removeField = (idx) => patch({ fields: ev.fields.filter((_, i) => i !== idx) });
  const moveField = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= ev.fields.length) return;
    const next = [...ev.fields];
    [next[idx], next[j]] = [next[j], next[idx]];
    patch({ fields: next });
  };

  const save = async (openIt) => {
    if (!ev.title.trim()) {
      setErr('Please give the event a title.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const nextStatus = openIt ? 'open' : (ev.status === 'archived' ? 'draft' : ev.status);
      const payload = { ...ev, status: nextStatus };
      if (editing) await updateEvent(id, payload, user);
      else await createEvent(payload, user);
      navigate('/events');
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="ev">
      <div className="ev__inner">
        <header className="ev__hero">
          <div>
            <button className="ev-linkbtn ev-linkbtn--back" onClick={() => navigate('/events')}>← {t('back')}</button>
            <h1 className="ev__title"><span className="ev__title-grad">{editing ? t('editEventTitle') : t('newEventTitle')}</span></h1>
          </div>
          <div className="ev__hero-actions">
            <button className="ev__btn ev__btn--sm ev-only-mobile" onClick={() => setShowPreview(true)}>👁 {t('preview')}</button>
            {editing && <button className="ev__btn ev__btn--sm" onClick={() => setShowShare(true)}>🔗 {t('share')}</button>}
          </div>
        </header>

        {err && <div className="ev__error">{err}</div>}

        <div className="ev-build">
          <div className="ev-build__main">
            {/* Basics */}
            <section className="ev-section">
              <h2 className="ev-section__title">{t('basics')}</h2>
              <label className="ev-lbl">{t('eventTitleLabel')}</label>
              <input className="ev-input" placeholder={t('eventTitlePlaceholder')}
                value={ev.title} onChange={(e) => patch({ title: e.target.value })} />
              <label className="ev-lbl">{t('descriptionLabel')}</label>
              <textarea className="ev-input" rows={2} placeholder={t('descriptionPlaceholder')}
                value={ev.description} onChange={(e) => patch({ description: e.target.value })} />
              <div className="ev-row">
                <label className="ev-minifield">
                  <span>{t('accentColor')}</span>
                  <input type="color" className="ev-color" value={ev.bannerColor || '#f59e0b'}
                    onChange={(e) => patch({ bannerColor: e.target.value })} />
                </label>
                <label className="ev-minifield ev-minifield--grow">
                  <span>{t('deadlineField')}</span>
                  <input type="datetime-local" className="ev-input ev-input--sm"
                    value={msToLocalInput(ev.deadline)}
                    onChange={(e) => patch({ deadline: localInputToMs(e.target.value) })} />
                </label>
              </div>
            </section>

            {/* Menu — structured items and/or a link */}
            <section className="ev-section">
              <h2 className="ev-section__title">{t('menuItemsTitle')}</h2>
              <p className="ev-section__hint">{t('menuItemsHint')}</p>
              <label className="ev-lbl">{t('menuLinkLabel')}</label>
              <input className="ev-input" type="url" placeholder={t('menuLinkPlaceholder')}
                value={ev.menuLink || ''} onChange={(e) => patch({ menuLink: e.target.value })} />
              <p className="ev-section__hint" style={{ margin: '6px 0 14px' }}>{t('menuLinkHint')}</p>

              <div className="ev-menuload">
                {menus.length > 0 && (
                  <>
                    <select className="ev-input ev-input--sm" value={loadMenuId} onChange={(e) => setLoadMenuId(e.target.value)}>
                      <option value="">{t('loadMenu')}</option>
                      {menus.map((m) => <option key={m.id} value={m.id}>{m.name} ({(m.items || []).length})</option>)}
                    </select>
                    <button className="ev__btn ev__btn--sm" type="button" disabled={!loadMenuId} onClick={loadMenu}>{t('load')}</button>
                  </>
                )}
                <button className="ev__btn ev__btn--sm" type="button" disabled={!(ev.menuItems || []).length} onClick={() => setShowSaveMenu(true)}>
                  💾 {t('saveAsMenu')}
                </button>
              </div>

              <MenuItemsEditor items={ev.menuItems || []} onChange={(menuItems) => patch({ menuItems })} />
            </section>

            {/* Custom questions */}
            <section className="ev-section">
              <h2 className="ev-section__title">{t('formFieldsTitle')}</h2>
              <p className="ev-section__hint">{t('formFieldsHint')}</p>
              {(ev.fields || []).map((f, i) => (
                <FieldEditor
                  key={f.id}
                  field={f}
                  index={i}
                  count={ev.fields.length}
                  onChange={(nf) => updateField(i, nf)}
                  onRemove={() => removeField(i)}
                  onMove={(dir) => moveField(i, dir)}
                />
              ))}
              <div className="ev-addrow">
                <select className="ev-input ev-input--sm" value={addType} onChange={(e) => setAddType(e.target.value)}>
                  {FIELD_TYPES.map((ft) => <option key={ft.type} value={ft.type}>{ft.icon} {ft.label}</option>)}
                </select>
                <button className="ev-addbtn" onClick={addField}>+ {t('addField')}</button>
              </div>
            </section>

            {/* Settings */}
            <section className="ev-section">
              <h2 className="ev-section__title">{t('settingsTitle')}</h2>
              <div className="ev-toggles">
                {SETTING_TOGGLES.map((key) => (
                  <label key={key} className="ev-check">
                    <input type="checkbox" checked={!!ev.settings?.[key]}
                      onChange={(e) => patchSettings({ [key]: e.target.checked })} />
                    <span>{t(key)}</span>
                  </label>
                ))}
              </div>
              <div className="ev-row">
                <label className="ev-minifield ev-minifield--grow">
                  <span>{t('payNoteLabel')}</span>
                  <input className="ev-input ev-input--sm" placeholder={t('payNotePlaceholder')}
                    value={ev.settings?.payNote || ''} onChange={(e) => patchSettings({ payNote: e.target.value })} />
                </label>
                <label className="ev-minifield">
                  <span>{t('capacityLabel')}</span>
                  <input className="ev-input ev-input--sm" type="number" min="1" placeholder="∞"
                    value={ev.settings?.capacity ?? ''}
                    onChange={(e) => patchSettings({ capacity: e.target.value === '' ? null : Number(e.target.value) })} />
                </label>
              </div>
            </section>

            {editing && (ev.history || []).length > 0 && (
              <section className="ev-section">
                <h2 className="ev-section__title">🕘 {t('activity')}</h2>
                <EventTimeline history={ev.history} />
              </section>
            )}

            <div className="ev-savebar">
              <button className="ev__btn" onClick={() => navigate('/events')} disabled={saving}>{t('cancel')}</button>
              <button className="ev__btn" onClick={() => save(false)} disabled={saving}>{t('save')}</button>
              <button className="ev__btn ev__btn--primary" onClick={() => save(true)} disabled={saving}>
                {saving ? '…' : t('saveAndOpen')}
              </button>
            </div>
          </div>

          {/* Live preview (desktop) */}
          <aside className="ev-build__aside">
            <div className="ev-build__preview-card">
              <div className="ev-build__preview-head">👁 {t('livePreview')}</div>
              <FormPreview event={ev} />
            </div>
          </aside>
        </div>
      </div>

      {editing && <ShareModal open={showShare} onClose={() => setShowShare(false)} eventId={id} title={ev.title} />}
      <Modal open={showPreview} onClose={() => setShowPreview(false)} title={t('livePreview')} maxWidth={420}>
        <FormPreview event={ev} />
      </Modal>
      <MenuEditorModal
        open={showSaveMenu}
        initial={{ name: '', items: ev.menuItems || [] }}
        onClose={() => setShowSaveMenu(false)}
        onSave={async (data) => { await createMenu(data, user); }}
      />
    </div>
  );
}
